import type {} from "jquery";
import {
  type MaybeRef,
  type MaybeRefOrGetter,
  type Reactive,
  computed,
  isReactive,
  reactive,
  ref,
  toValue,
  unref,
  watch,
  provide
} from "vue";

import { isAnyHTMLInputElementType, isFunction, isNil, isString, type Nil } from "@mcwd/typescript-type-guards";

import
  {
  injectRequiredWithSelf,
  checkValuesAlreadyProvided,
  CreateInjectionKeysRecord,
  type InjectionValuesRecord,
  type InjectionKeysRecord,
  type MaybeReactive,
  type UnwrapReactive
} from "@mcwd/util-vue";
 //import { logReactiveChanges } from "@mcwd/util-vue";

import { useActiveElement } from "@vueuse/core";
import { useGatingLevel } from "./use-gating-level.js";
import { useFormEvents } from "./use-form-events.js";
import { useEclipseHtmlFormElement } from "./use-eclipse-html-form-element.js";
import type { ILogger } from "../../utils/Logger/ILogger.js";
import { useFormLogger } from "./use-form-logger.js";
import type { ValidationOutput, ValidatorFn } from "./use-input-validation.js";

const activeElement = useActiveElement();
export interface InputState {
  inputValue: any;
  inputName: string;
  required: MaybeRefOrGetter<boolean>;
  active: boolean;
  touched: boolean;
  done: boolean;
  hasError: boolean;
  showError: boolean;
  isEmpty: boolean;
  validationCheck: ValidationOutput;
}
export type InputStateKey = keyof InputState;

function isActiveElementMatch(formElement: HTMLFormElement, htmlElement: HTMLElement, inputName: string, activeInputVerboseLogger: ILogger) {
  if (!isAnyHTMLInputElementType(htmlElement)) {
    activeInputVerboseLogger.debug("Is not an input element, not an active element match", { formElement, htmlElement, inputName });
    return false;
  }
  if (!formElement.contains(htmlElement)) {
    activeInputVerboseLogger.debug("Is not contained in the form element, not an active element match", { formElement, htmlElement, inputName });
    return false;
  }

  if (htmlElement.getAttribute("data-active-element-match-name") === inputName) {
    activeInputVerboseLogger.debug("Is an active element match, data attr matches", { formElement, htmlElement, inputName });
    return true;
  }
  else if (htmlElement.id === inputName) {
    activeInputVerboseLogger.debug("Is an active element match, id matches", { formElement, htmlElement, inputName });
    return true;
  }
  else if (htmlElement.name === inputName) {
    activeInputVerboseLogger.debug("Is an active element match, name matches", { formElement, htmlElement, inputName });
    return true;
  }
  else {
    activeInputVerboseLogger.debug("Is not an active element match, name does not match", { formElement, htmlElement, inputName });
    return false;
  }
}

type NewInputStateArgs<TEnabledFormField extends string> = {
  formElement: MaybeRefOrGetter<HTMLFormElement | Nil>;
  formValues: Reactive<Record<TEnabledFormField, unknown>>;
  inputName: TEnabledFormField;
  required: MaybeRefOrGetter<boolean>;
  validator?: MaybeRef<ValidatorFn>;
  logger: ILogger
};


function newInputState<TEnabledFormField extends string>({
  formElement,
  formValues,
  inputName,
  required,
  validator,
  logger
}: NewInputStateArgs<TEnabledFormField>): InputState {
  if (isReactive(formValues) === false) {
    throw new Error(`formValues is not reactive`);
  }

  const inputValue = computed(() => {
    return formValues[inputName];
  });

  const activeInputVerboseLogger = logger.withTags({ "verboseDebug": "activeInput" });
  const active = computed(() => {
    const formEl = toValue(formElement);
    if (isNil(activeElement.value) || isNil(formEl)) {
      return false;
    }
    return isActiveElementMatch(formEl, activeElement.value, inputName, activeInputVerboseLogger);
  });

  const touched = ref<boolean>(false);

  watch(
    () => active.value,
    (isActive) => {
      if (isActive) {
        touched.value = true;
      }
    }
  );

  watch(
    () => inputValue.value,
    (value, prevValue) => {
      if (value !== prevValue) {
        touched.value = true;
      }
    }
  );

  const isEmpty = computed(() => {
    return (
      isNil(inputValue.value) ||
      (isString(inputValue.value) && inputValue.value.trim() === "")
    );
  });

  const hasRequiredError = computed(() => {
    return (toValue(required) === true && isEmpty.value);
  });

  const validatorFn = unref(validator);
  const validationCheck = computed<ValidationOutput>(() => {
    if(isFunction(validatorFn)){
      const validationResults = validatorFn(inputValue.value);
      return validationResults;
    }
    return { isValid: true };
  });

  const hasValidatorError = computed<boolean>(()=>{
    return !validationCheck.value.isValid;
  });

  const hasError = computed(() => {
    return hasRequiredError.value || hasValidatorError.value;
  });

  const errorVisibilityWithoutActiveCheck = computed(() => {
    if (hasError.value === false) {
      return false;
    }

    if(touched.value) {
      return true;
    }
    if(!hasValidatorError.value) {
      return isEmpty.value === false;
    }
    return false;
  });

  const errorVisibilityForInactiveInput = computed(() => {
    if (active.value === true) {
      return false;
    }
    return errorVisibilityWithoutActiveCheck.value;
  });
  const wasAlreadyDisplayingError = ref<boolean>(false);

  watch(
    () => errorVisibilityForInactiveInput.value,
    (currentInactiveVisibility, prevInactiveVisibility) => {
      wasAlreadyDisplayingError.value = prevInactiveVisibility;
    }
  );

  const errorVisibilityForActiveOrInactiveInput = computed(() => {
    // If input is active and was already displaying an error, keep displaying it if the error condition still exists
    // If the error was not visible before the input became active, don't start showing it now.
    if (active.value === true && wasAlreadyDisplayingError.value === true) {
      return errorVisibilityWithoutActiveCheck.value;
    }
    // Otherwise, follow the logic for an inactive input.
    else {
      return errorVisibilityForInactiveInput.value;
    }
  });

  const done = computed(() => {
    if (hasError.value === false && toValue(required) === false) {
      return false;
    } else {
      return hasError.value === false;
    }
  });

  // logReactiveChanges(inputValue, "inputValue", `${inputName}: `);
  // logReactiveChanges(active, "active", `${inputName}: `);
  // logReactiveChanges(done, "done", `${inputName}: `);
  //  logReactiveChanges(hasError, "hasError", `${inputName}: `);
  //  logReactiveChanges(
  //   errorVisibilityForActiveOrInactiveInput,
  //   "showError",
  //   `${inputName}: `
  // );
  // logReactiveChanges(touched, "touched", `${inputName}: `);
  return reactive({
    inputValue,
    inputName,
    required,
    active,
    done,
    isEmpty,
    hasError,
    wasAlreadyDisplayingError,
    validationCheck,
    hasRequiredError,
    errorVisibilityWithoutActiveCheck,
    errorVisibilityForInactiveInput,
    errorVisibilityForActiveOrInactiveInput,
    touched,
    showError: errorVisibilityForActiveOrInactiveInput,
  });
}

export type InputStateOption = {
  required: MaybeRefOrGetter<boolean>;
  validator?: MaybeRef<ValidatorFn>;  
};

type UseInputStateInjectionValues<TEnabledFormField extends string = string> =
  InjectionValuesRecord<{
    inputStates: Record<TEnabledFormField, InputState>;
    setInputStates(
      inputValues: MaybeReactive<Record<TEnabledFormField, unknown>>,
      inputStateOptions: Record<TEnabledFormField, InputStateOption>
    );
    watchForFirstUserInteraction(inputStatesObj: Record<string, InputState>): void
  }>;

type UseInputStateInjectionKeys<TEnabledFormField extends string = string> = InjectionKeysRecord<UseInputStateInjectionValues<TEnabledFormField>>;

const UseInputStateInjectionKeys = CreateInjectionKeysRecord<UseInputStateInjectionKeys>({
  inputStates: "inputStates",
  setInputStates: "setInputStates",
  watchForFirstUserInteraction: "watchForFirstUserInteraction"
});


export function initInputState() {
  if (checkValuesAlreadyProvided(UseInputStateInjectionKeys)) {
    return;
  }
  const { logger } = useFormLogger();

  const inputStates = reactive({} as Record<string, InputState>);

  const { EclipseHtmlFormElement } = useEclipseHtmlFormElement({ callerLabel: "use-input-states" });
  function setInputStates<
    TEnabledFormField extends string,
    TInputValues extends MaybeReactive<Record<TEnabledFormField, unknown>>
  >(
    inputValues: TInputValues,
    inputStateOptions: Record<TEnabledFormField, InputStateOption>
  ) 
  {
    let reactiveInputValues: Reactive<TInputValues>;
    if (isReactive(inputValues)) {
      reactiveInputValues = inputValues as UnwrapReactive<typeof inputValues>;
    }
    else {
      const { CurrentMarketoFormGatingState } = useGatingLevel({
        callerLabel: "setInputState",
      });
      if (CurrentMarketoFormGatingState.value === "BlindSubmit") {
        reactiveInputValues = reactive((inputValues ?? {}) as Record<TEnabledFormField, unknown>);
      } else {
        throw new Error(`inputValues is not reactive`);
      }
    }

    for (const key of Object.keys(inputStates)) {
      delete inputStates[key];
    }

    const EnabledFormFields = Object.keys(
      inputStateOptions
    ) as TEnabledFormField[];

    for (const field of EnabledFormFields) {
      inputStates[field] = newInputState({
        logger,
        formElement: EclipseHtmlFormElement,
        formValues: reactiveInputValues,
        inputName: field,
        ...inputStateOptions[field],
      });
    }
  }

  function watchForFirstUserInteraction(inputStatesObj: Record<string, InputState>) {
    const inputStatesKeys = computed(() => {
      return Object.keys(inputStatesObj);
    });
    const anyInputActive = computed(() => {
      return inputStatesKeys.value
        .map(key => inputStatesObj[key].active)
        .filter(a => a === true)
        .length > 0;
    });
    const { userFirstInteraction } = useFormEvents({ callerLabel: "use-input-state" });
    const removeWatcher = watch(() => anyInputActive.value, (anyActive) => {
      if (anyActive) {
        removeWatcher();
        userFirstInteraction.trigger({ eventName: "userFirstInteraction" }).catch((e) =>logger.error(e));
      }
    });
  }

  watchForFirstUserInteraction(inputStates);

  provide(UseInputStateInjectionKeys.inputStates, inputStates);
  provide(UseInputStateInjectionKeys.setInputStates, setInputStates);
  provide(UseInputStateInjectionKeys.watchForFirstUserInteraction, watchForFirstUserInteraction);
}
export function useInputState<TEnabledFormField extends string> ({ callerLabel }: { callerLabel?: string | undefined } = {}) {
  const { logger } = useFormLogger({ callerLabel: "useInputState" });
  try {
    const inputStates = injectRequiredWithSelf(
      UseInputStateInjectionKeys.inputStates as UseInputStateInjectionKeys<TEnabledFormField>["inputStates"]
    );
    const setInputStates = injectRequiredWithSelf(
      UseInputStateInjectionKeys.setInputStates as UseInputStateInjectionKeys<TEnabledFormField>["setInputStates"]
    );

    return { inputStates, setInputStates };
  }
  catch (err) {
    logger.error(`InjectionError from ${callerLabel ?? "unknown"}`);
    throw err;
  }
}
